/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  IconBarcode,
  IconChevronLeft,
  IconCloudUpload,
  IconEdit,
} from "@tabler/icons-react";
import TextareaField from "../../components/common/TextareaField";
import Input from "../../components/Input";
import SelectInput from "../../components/Select";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import {
  useItemProductCreateMutation,
  useItemsUpdateMutation,
  useProductSubCategoriesListData,
  useProductCategoryAllListData,
  useFetchProductItemsViewData,
} from "../../apis/productApis";
import { useWarehouseListData } from "../../apis/warehouseApi";
import toast from "react-hot-toast";

type ItemForm = {
  image: File | string;
  itemName: string;
  categoryId: string;
  subCategory?: string;
  // warehouseId: string;
  storageType: string;
  sku?: string;
  capacity: string;
  minimumQuantity: string;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number;
  commodity: string;
  importCommodity: string;
  dangerousGoods: string;
  hsCode: string;
  generalDescription: string;
  skuDescription: string;
  itemInfo: Record<string, any>;
  variants?: Array<{
    id: string;
    optionName: string;
    optionValue: string;
    displayValue: string;
    sku?: string;
    price: string;
    salePrice: string | null;
    regularPrice: string;
    stockQuantity: number;
    unlimitedQuantity: boolean;
    isOutOfStock: boolean;
    weight: string;
    itemInfo: Record<string, any>;
  }>;

  [key: string]: any;
};

const storageTypes = [
  { id: 1, value: "SHELVES", text: "SHELVES" },
  { id: 2, value: "PALLETS", text: "PALLETS" },
  { id: 3, value: "FLOORS", text: "FLOORS" },
  { id: 4, value: "BINS", text: "BINS" },
];

const dangerousGoods = [
  { id: 1, value: "YES", text: "YES" },
  { id: 2, value: "NO", text: "NO" },
];

const AddItem: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: warehouses } = useWarehouseListData();
  const { data: allCategory } = useProductCategoryAllListData();
  const { data: product, refetch: fetchData } =
    useFetchProductItemsViewData(id);
  const [isEditing, setIsEditing] = useState(!id);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm<ItemForm>({
    defaultValues: {
      itemInfo: {},
      variants: [],
    },
  });

  const selectedCategory = watch("categoryId");
  const selectedSubCategory = watch("subCategory");
  const { data: subCategories } = useProductSubCategoriesListData(
    selectedCategory || "",
  );
  const { mutate: createItem, isPending: creating } =
    useItemProductCreateMutation();
  const { mutate: updateItem, isPending: updating } = useItemsUpdateMutation(
    id || "",
  );

  const [previewUrl, setPreviewUrl] = useState<string>(
    product?.image || "/img/dummy_logo.png",
  );

  useEffect(() => {
    if (product) {
      setValue("categoryId", product.category?.id || product.category || "");
      setValue(
        "subCategory",
        product.subCategory?.id || product.categoryId || "",
      );
      // setValue("warehouseId", product.warehouseId || "");
      setValue("storageType", product.storageType || "");
      setValue("dangerousGoods", product.dangerousGoods || "");
      setValue("itemName", product.itemName || "");
      setValue("capacity", product.capacity || "");
      setValue("minimumQuantity", product.minimumQuantity || "");
      setValue("length", product.length ?? null);
      setValue("width", product.width ?? null);
      setValue("height", product.height ?? null);
      setValue("weight", product.weight || 0);
      setValue("commodity", product.commodity || "");
      setValue("importCommodity", product.importCommodity || "");
      setValue("hsCode", product.hsCode || "");
      setValue("generalDescription", product.generalDescription || "");
      setValue("skuDescription", product.skuDescription || "");
      setValue("image", product.image || "");
      setValue("variants", product.variants || []);
      setValue("sku", product?.sku || "");

      if (product.itemInfo) {
        Object.entries(product.itemInfo).forEach(([key, value]) => {
          setValue(key, value);
          setValue("itemInfo", { ...watch("itemInfo"), [key]: value });
        });
      }
    }
  }, [product, setValue, watch]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrl && typeof previewUrl !== "string") {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleEditToggle = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (product) {
      setValue("categoryId", product.category?.id || product.category || "");
      setValue(
        "subCategory",
        product.subCategory?.id || product.categoryId || "",
      );
      // setValue("warehouseId", product.warehouseId || "");
      setValue("storageType", product.storageType || "");
      setValue("dangerousGoods", product.dangerousGoods || "");
      setValue("itemName", product.itemName || "");
      setValue("capacity", product.capacity || "");
      setValue("minimumQuantity", product.minimumQuantity || "");
      setValue("length", product.length ?? null);
      setValue("width", product.width ?? null);
      setValue("height", product.height ?? null);
      setValue("weight", product.weight || 0);
      setValue("commodity", product.commodity || "");
      setValue("importCommodity", product.importCommodity || "");
      setValue("hsCode", product.hsCode || "");
      setValue("generalDescription", product.generalDescription || "");
      setValue("skuDescription", product.skuDescription || "");
      setValue("image", product.image || "");
      setValue("variants", product.variants || []);

      if (product.itemInfo) {
        Object.entries(product.itemInfo).forEach(([key, value]) => {
          setValue(key, value);
          setValue("itemInfo", { ...watch("itemInfo"), [key]: value });
        });
      }
    }
    setIsEditing(false);
  };

  const onSubmit = (values: ItemForm) => {
    const subCat = subCategories?.find(
      (sc: any) => sc.id === values.subCategory,
    );
    const dynamicFields: Record<string, any> = {};
    subCat?.fields?.forEach((field: string) => {
      dynamicFields[field] = values[field] || "";
    });
    Object.entries(values.itemInfo).forEach(([key, value]) => {
      dynamicFields[key] = value;
    });

    const payload = {
      itemName: values.itemName,
      categoryId:
        allCategory?.find((c: any) => c.category === values.categoryId)?.id ||
        values.categoryId,
      // warehouseId: values.warehouseId,
      storageType: values.storageType,
      capacity: values.capacity,
      minimumQuantity: values.minimumQuantity,
      length: values.length !== null ? Number(values.length) : null,
      width: values.width !== null ? Number(values.width) : null,
      height: values.height !== null ? Number(values.height) : null,
      weight: Number(values.weight),
      commodity: values.commodity,
      importCommodity: values.importCommodity,
      dangerousGoods: values.dangerousGoods,
      hsCode: values.hsCode,
      generalDescription: values.generalDescription,
      skuDescription: values.skuDescription,
      sku: values.sku,
      itemInfo: dynamicFields,
      image: values.image,
      variants: values.variants,
    };

    if (id) {
      updateItem(payload, {
        onSuccess: () => {
          console.log("✅ Item updated successfully");
          navigate("/inventory-mangement");
          toast.success("Item Update Successfully.");
        },
        onError: (err: any) => {
          console.error("❌ Item update failed:", err);
        },
      });
    } else {
      createItem(payload, {
        onSuccess: () => {
          console.log("✅ Item created successfully");
          navigate("/inventory-mangement");
          toast.success("Item Add Successfully.");
        },
        onError: (err: any) => {
          console.error("❌ Item create failed:", err);
        },
      });
    }
  };

  const dynamicFields =
    subCategories?.find((sc: any) => sc.id === selectedSubCategory)?.fields ||
    [];

  const formatTitle = (key: string): string => {
    // Replace underscores with spaces and split camelCase
    const formatted = key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters for camelCase
      .replace(/_/g, " ") // Replace underscores with spaces
      .trim() // Remove leading/trailing spaces
      .split(" ") // Split into words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(" "); // Join words with spaces
    return formatted;
  };
  const variants = watch("variants");

  console.log(dynamicFields, "d", watch("itemInfo"));

  return (
    <div className="w-full flex justify-center px-6 pb-6 space-y-4">
      <div className="w-[1280px] space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate("/inventory-mangement")}
              className="p-1.5 border border-[#D2D2D2] rounded-full"
            >
              <IconChevronLeft strokeWidth={1.5} size={24} />
            </button>
            <p className="text-base font-medium text-[#212121] tracking-[-0.5px]">
              {id ? "Edit Item" : "Add New Item"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {id && !isEditing ? (
              <>
                <a
                  href={product?.barcode}
                  target="blank"
                  title="Barcode Download"
                  className="px-6 py-2 text-base rounded-full bg-[#FBD037] text-[#212121] flex items-center gap-2"
                >
                  <IconBarcode />
                  <span>Download</span>
                </a>
                <button
                  onClick={handleEditToggle}
                  className="px-6 py-2 text-base rounded-full bg-[#FBD037] text-[#212121] flex items-center gap-2"
                >
                  <IconEdit size={18} />
                  Edit
                </button>
              </>
            ) : (
              <>
                <button className="px-6 py-2 text-base rounded-full bg-[#FBD037] text-[#212121]">
                  Draft
                </button>
                {id && isEditing && (
                  <button
                    onClick={handleCancelEdit}
                    className="px-6 py-2 text-base rounded-full bg-gray-500 text-white"
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleSubmit(onSubmit)}
                  disabled={creating || updating}
                  className="px-6 py-2 text-base rounded-full bg-[#212121] text-white"
                >
                  {creating || updating ? "Saving..." : id ? "Update" : "Save"}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-3 grid grid-cols-4 gap-4">
            <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 col-span-1 h-full">
              <h2 className="uppercase font-medium text-base">Items Image</h2>
              <Controller
                control={control}
                name="image"
                rules={{
                  required: "Items Image is required",
                  validate: (file: File | string) => {
                    if (!file || file === "") return "Please select a file";
                    if (typeof file === "string") return true;
                    const allowedTypes = [
                      "image/jpeg",
                      "image/png",
                      "image/jpg",
                      "application/pdf",
                    ];
                    if (!allowedTypes.includes(file.type)) {
                      return "Only JPG, JPEG, PDF, or PNG allowed";
                    }
                    if (file.size > 1024 * 1024) {
                      return "File size must be less than 1MB";
                    }
                    return true;
                  },
                }}
                render={({ field, fieldState }) => {
                  const fileURL =
                    field.value instanceof File
                      ? URL.createObjectURL(field.value)
                      : typeof field.value === "string" && field.value !== ""
                        ? field.value
                        : "/img/dummy_logo.png";

                  return (
                    <div className="w-full">
                      <label
                        htmlFor="photoUpload"
                        className={`p-4 aspect-square w-full rounded-lg border border-dashed h-[85%] bg-white flex items-center justify-center flex-col gap-2 ${
                          isEditing
                            ? "cursor-pointer"
                            : "cursor-not-allowed opacity-50"
                        }`}
                      >
                        <input
                          id="photoUpload"
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0] && isEditing) {
                              field.onChange(e.target.files[0]);
                              setPreviewUrl(
                                URL.createObjectURL(e.target.files[0]),
                              );
                            }
                          }}
                          disabled={!isEditing}
                        />
                        {field.value && field.value !== "" ? (
                          <img
                            src={fileURL}
                            className="size-60 rounded-lg object-cover"
                            alt="preview"
                          />
                        ) : (
                          <div className="bg-[#FBD037]/20 rounded-md p-2 px-4">
                            <IconCloudUpload strokeWidth={1.5} size={28} />
                          </div>
                        )}
                        <p className="text-xs text-center">
                          Accepted formats: png & jpg Max: 1MB
                        </p>
                      </label>
                      {field.value && field.value !== "" && isEditing && (
                        <button
                          type="button"
                          className="text-red-500 text-sm mt-2"
                          onClick={() => {
                            field.onChange("");
                            setPreviewUrl("/img/dummy_logo.png");
                          }}
                        >
                          Remove
                        </button>
                      )}
                      {fieldState.error && (
                        <p className="text-xs text-red-500 mt-1">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>

            <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 col-span-3  z-30">
              <h2 className="uppercase font-medium text-base">
                Identification
              </h2>
              <div className="grid grid-cols-3 gap-4">
                {isEditing ? (
                  <SelectInput
                    name="categoryId"
                    label="Category"
                    placeholder="Select Category"
                    selectOptions={
                      allCategory?.map((items: any) => ({
                        id: items?.id,
                        value: items?.category,
                        text: items?.category,
                      })) || []
                    }
                    register={register}
                    clearErrors={clearErrors}
                    validation={{ required: "Category is required" }}
                    error={errors.categoryId}
                    value={watch("categoryId")}
                    setValue={(val: string) => {
                      if (isEditing) {
                        setValue("categoryId", val);
                        setValue("subCategory", "");
                        setValue("itemInfo", {});
                      }
                    }}
                    disabled={!isEditing}
                  />
                ) : (
                  <Input
                    name="categoryId"
                    title="Category"
                    placeholder="Select Category"
                    disabled
                    value={
                      allCategory?.find(
                        (c: any) => c.id === watch("categoryId"),
                      )?.category || watch("categoryId")
                    }
                    inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  />
                )}

                {isEditing ? (
                  <SelectInput
                    name="subCategory"
                    label="Sub Category"
                    placeholder={
                      !selectedCategory
                        ? "Select Category first"
                        : "Select Sub Category"
                    }
                    selectOptions={(subCategories || [])?.map((items: any) => ({
                      id: items?.id,
                      value: items?.id,
                      text: items?.subCategory,
                    }))}
                    register={register}
                    clearErrors={clearErrors}
                    validation={{ required: "Sub Category is required" }}
                    error={errors.subCategory}
                    value={watch("subCategory")}
                    setValue={(val: string) => {
                      if (isEditing) {
                        setValue("subCategory", val);
                        setValue("itemInfo", {});
                      }
                    }}
                    disabled={!selectedCategory || !isEditing}
                  />
                ) : (
                  <Input
                    name="subCategory"
                    title="Sub Category"
                    placeholder="Select Sub Category"
                    disabled
                    value={
                      subCategories?.find(
                        (c: any) => c.id === watch("subCategory"),
                      )?.subCategory || watch("subCategory")
                    }
                    inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  />
                )}
                <Input
                  name="itemName"
                  type="text"
                  title="Item Name"
                  register={register}
                  error={errors.itemName}
                  validation={{ required: "Item Name is required" }}
                  inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                    isEditing
                      ? "bg-white border border-[#D8D8D8]"
                      : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  }`}
                  disabled={!isEditing}
                />
              </div>

              {selectedSubCategory &&
                dynamicFields.length > 0 &&
                Object.keys(watch("itemInfo"))?.length === 0 && (
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    {dynamicFields.map((field: string, idx: number) => (
                      <Input
                        key={idx}
                        name={field}
                        type="text"
                        title={field}
                        placeholder={`Enter ${field}`}
                        register={register}
                        error={errors[field as keyof ItemForm]}
                        validation={{ required: `${field} is required` }}
                        inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                          isEditing
                            ? "bg-white border border-[#D8D8D8]"
                            : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                        }`}
                        disabled={!isEditing}
                      />
                    ))}
                  </div>
                )}

              <div className="grid grid-cols-3 gap-4 mt-4">
                <Input
                  name="sku"
                  type="text"
                  title="SKU"
                  register={register}
                  error={errors.sku}
                  validation={{ required: "Sku is required" }}
                  inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                    isEditing
                      ? "bg-white border border-[#D8D8D8]"
                      : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                  }`}
                  disabled={id ? true : false}
                />

                {Object.entries(watch("itemInfo") || {}).map(
                  ([key, value], idx) => (
                    <Input
                      key={`itemInfo-${idx}`}
                      name={`itemInfo.${key}`}
                      type="text"
                      title={formatTitle(key)} // Use formatted title
                      placeholder={`Enter ${formatTitle(key)}`}
                      register={register}
                      error={
                        errors.itemInfo?.[key as keyof ItemForm["itemInfo"]]
                      }
                      validation={{ required: `${key} is required` }}
                      inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                        isEditing
                          ? "bg-white border border-[#D8D8D8]"
                          : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                      }`}
                      disabled={!isEditing}
                    />
                  ),
                )}
              </div>
            </div>
          </div>

          <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 z-20">
            <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
              Storage & Warehouse
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {/* {isEditing ? (
                <SelectInput
                  className="col-span-2"
                  name="warehouseId"
                  label="Select Warehouse"
                  placeholder=""
                  selectOptions={
                    warehouses?.map((w: any) => ({
                      id: w?.id,
                      value: w?.id,
                      text: w?.name,
                    })) || []
                  }
                  register={register}
                  clearErrors={clearErrors}
                  validation={{ required: "Warehouse is required" }}
                  error={errors.warehouseId}
                  disabled={
                    !warehouses || warehouses?.length === 0 || !isEditing
                  }
                  value={watch("warehouseId")}
                  setValue={(val: string) => {
                    if (isEditing) setValue("warehouseId", val);
                  }}
                />
              ) : (
                <Input
                  className="col-span-2"
                  name="warehouseId"
                  title="Warehouse"
                  placeholder="Select warehouse"
                  disabled
                  value={
                    warehouses?.find((w: any) => w.id === watch("warehouseId"))
                      ?.name || watch("warehouseId")
                  }
                  inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                />
              )} */}
              {isEditing ? (
                <SelectInput
                  className="col-span-2"
                  name="storageType"
                  label="Storage Type"
                  placeholder=""
                  selectOptions={storageTypes}
                  clearErrors={clearErrors}
                  register={register}
                  validation={{ required: "Storage type is required" }}
                  error={errors.storageType}
                  value={watch("storageType")}
                  setValue={(val: string) => {
                    if (isEditing) setValue("storageType", val);
                  }}
                  disabled={!isEditing}
                />
              ) : (
                <Input
                  className="col-span-2"
                  name="storageType"
                  title="Storage Type"
                  placeholder="Select Storage Type"
                  disabled
                  value={watch("storageType")}
                  inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                />
              )}
              <Input
                name="capacity"
                type="text"
                title="Capacity"
                placeholder="Enter Capacity"
                register={register}
                error={errors.capacity}
                validation={{ required: "Capacity is required" }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
              <Input
                name="minimumQuantity"
                type="number"
                title="Minimum Quantity"
                placeholder="Number (Alert)"
                register={register}
                error={errors.minimumQuantity}
                validation={{ required: "Minimum Quantity is required" }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4">
            <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
              Dimensions & Weight
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Input
                name="length"
                type="number"
                title="Length"
                placeholder="Number"
                register={register}
                error={errors.length}
                validation={{ required: false }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
              <Input
                name="width"
                type="number"
                title="Width"
                placeholder="Number"
                register={register}
                error={errors.width}
                validation={{ required: false }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
              <Input
                name="height"
                type="number"
                title="Height"
                placeholder="Number"
                register={register}
                error={errors.height}
                validation={{ required: false }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
              <Input
                name="weight"
                type="number"
                title="Weight"
                placeholder="Number"
                register={register}
                error={errors.weight}
                validation={{ required: "Weight is required" }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4">
            <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
              Compliance & Codes
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {isEditing ? (
                <SelectInput
                  className="col-span-2"
                  name="dangerousGoods"
                  label="Dangerous Goods"
                  placeholder=""
                  selectOptions={dangerousGoods}
                  clearErrors={clearErrors}
                  register={register}
                  validation={{ required: "Dangerous Goods type is required" }}
                  value={watch("dangerousGoods")}
                  error={errors.dangerousGoods}
                  setValue={(val: string) => {
                    if (isEditing) setValue("dangerousGoods", val);
                  }}
                  disabled={!isEditing}
                />
              ) : (
                <Input
                  name="dangerousGoods"
                  className="col-span-2"
                  title="Dangerous Goods"
                  placeholder="Select Dangerous Goods"
                  disabled
                  value={watch("dangerousGoods")}
                  inputClassName="px-4 py-2 text-sm w-full rounded-full outline-none opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                />
              )}
              <Input
                className="col-span-2"
                name="hsCode"
                type="text"
                title="HS Code"
                placeholder="Enter Code"
                register={register}
                error={errors.hsCode}
                validation={{ required: "HS Code is required" }}
                disabled={!isEditing}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
              />
              <Input
                name="commodity"
                type="text"
                title="Commodity"
                placeholder="Enter Code"
                register={register}
                error={errors.commodity}
                validation={{ required: "Commodity is required" }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
              <Input
                name="importCommodity"
                type="text"
                title="Import Commodity"
                placeholder="Enter Code"
                register={register}
                error={errors.importCommodity}
                validation={{ required: "Import Commodity is required" }}
                inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
                disabled={!isEditing}
              />
            </div>
          </div>

          {Array.isArray(variants) && variants.length > 0 && (
            <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 col-span-3">
              <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
                Variants
              </h2>

              <div className="grid grid-cols-2 gap-4">
                {watch("variants")?.map((variant, idx) => (
                  <React.Fragment key={idx}>
                    <Input
                      name={`variants[${idx}].sku`}
                      title="SKU"
                      value={variant.sku}
                      disabled={true}
                      inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                        isEditing
                          ? "bg-white border border-[#D8D8D8]"
                          : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                      }`}
                      register={register}
                      error={errors.variants?.[idx]?.sku}
                    />

                    <Input
                      name={`variants[${idx}].displayValue`}
                      title="Size"
                      value={variant.displayValue}
                      disabled={!isEditing}
                      inputClassName={`px-4 py-2 text-sm w-full rounded-full outline-none ${
                        isEditing
                          ? "bg-white border border-[#D8D8D8]"
                          : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                      }`}
                      register={register}
                      error={errors.variants?.[idx]?.displayValue}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 col-span-3">
            <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
              Variant Prices Chart
            </h2>
            {product?.variants && product.variants.length > 0 && (
              <ChartComponent product={product} />
            )}
          </div> */}

          <div className="border border-[#fff] backdrop-blur-sm rounded-[20px] p-4 bg-gradient-to-r from-[#FFFFFD]/70 to-[#FDF8E5]/70 space-y-4 col-span-3">
            <h2 className="uppercase font-medium text-base text-[#212121] tracking-[-0.5px]">
              Descriptions
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <TextareaField
                label="General Description"
                register={register}
                name="generalDescription"
                placeholder=""
                error={errors.generalDescription}
                disabled={!isEditing}
                inputClassName={`px-4 py-2 text-sm w-full rounded-xl outline-none resize-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
              />
              <TextareaField
                label="SKU Description"
                register={register}
                name="skuDescription"
                placeholder=""
                error={errors.skuDescription}
                disabled={!isEditing}
                inputClassName={`px-4 py-2 text-sm w-full rounded-xl outline-none resize-none ${
                  isEditing
                    ? "bg-white border border-[#D8D8D8]"
                    : "opacity-70 bg-gray-100 border border-[#D8D8D8] text-gray-700 cursor-not-allowed"
                }`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddItem;
